import {
  FullPropertyType,
  NUMERIC_MATH_TYPES,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  PropertyType,
  ResourceType,
  TYPECAST_LIST_PROPERTY_SUBTYPES,
  TYPECAST_PROPERTY_TYPES,
  XAU_MATH_TYPES,
} from 'common/report/constants';
import {FeatureRolloutName, FeatureRollouts} from 'common/report/util/feature-sampling';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {SectionItem} from 'common/components/items-menu/types';
import type {TooltipAttrs} from 'common/components/tooltip/types';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import segmentationSvg from 'common/assets/images/query/segmentation.svg';
import filterSvg from 'common/assets/images/query/filter.svg';
import {getIconForPropertyType, getLabelForPropertyType} from 'common/report/util/properties';
import {currentProjectStore} from 'common/report/stores/project-details';

import {AggregationLabelParams} from '../types/shared-types';
import {SelectorType, SelectorTypeAggregation, SelectorTypeDefinitions} from './types';

// Feature Naming
export const CUSTOM_BUCKET_NAMING = {
  entrypointName: `Customize Buckets`,
  bucketName: `bucket`,
};

export enum ControlBarAction {
  Filter = `filter`,
  FirstTimeFilter = `firstTimeFilter`,
  Rename = `rename`,
  Duplicate = `duplicate`,
  Delete = `delete`,
  CustomizeBuckets = `customizeBuckets`,
  CustomBreakdown = `customizeGroupings`,
  AddMultiAttribution = `addMultiAttribution`,
  AddMultiAttributionBehaviorFilter = `addMultiAttributionBehaviorFilter`,
  AddGroupedEvent = `addGroupedEvent`,
  MapLookupTable = `mapLookupTable`,
  UnmapLookupTable = `unmapLookupTable`,
  Typecast = `typecast`,
}

type TypeBasedAction = ControlBarAction.Typecast | ControlBarAction.CustomBreakdown;
export enum ControlSelectEntryPoint {
  CustomBucketSubItem = `customBucketSubItem`,
  ControlBar = `controlBar`,
}

export type ControlBarActionValue =
  | {action: ControlBarAction.Typecast; toType: FullPropertyType}
  | {action: Exclude<ControlBarAction, ControlBarAction.Typecast>};

const COMMON_CUSTOMIZE_GROUPS_TOOLTIP_PROPS = {size: DisplaySize.Small, 'delay-out': 500};

export const ControlBarActionItems:
  | Record<Exclude<ControlBarAction, TypeBasedAction>, SectionItem<ControlBarActionValue>>
  | Record<TypeBasedAction, (currentType: FullPropertyType, props?: Obj) => SectionItem<ControlBarActionValue>> = {
  [ControlBarAction.Filter]: {
    value: {action: ControlBarAction.Filter},
    label: `Filter`,
    shortLabel: `Filter`,
    icon: `filter-xs`,
  },
  [ControlBarAction.FirstTimeFilter]: {
    value: {action: ControlBarAction.FirstTimeFilter},
    label: `First Time Filter`,
    icon: `time`,
    tooltip: {
      text: `Only count a user's first time ever doing this event\n[More on 1st time filters](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#analyzing-first-time-users)`,
      size: DisplaySize.Small,
    },
  },
  [ControlBarAction.Rename]: {
    value: {action: ControlBarAction.Rename},
    icon: `edit`,
    label: `Rename`,
  },
  [ControlBarAction.Duplicate]: {
    value: {action: ControlBarAction.Duplicate},
    icon: `duplicate`,
    label: `Duplicate`,
  },
  [ControlBarAction.Delete]: {
    value: {action: ControlBarAction.Delete},
    icon: `x`,
    label: `Remove`,
  },
  [ControlBarAction.CustomizeBuckets]: {
    value: {action: ControlBarAction.CustomizeBuckets},
    icon: `bucketing`,
    label: `Customize Range`,
  },
  [ControlBarAction.CustomBreakdown]: (currentType: FullPropertyType) => {
    const simpleType = currentType.isList ? PropertyType.List : currentType.type;

    return {
      value: {action: ControlBarAction.CustomBreakdown},
      icon: `custom-segments`,
      label: CUSTOM_BUCKET_NAMING.entrypointName,
      tooltip: {
        [PropertyType.Number]: {
          ...COMMON_CUSTOMIZE_GROUPS_TOOLTIP_PROPS,
          text: `https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#numeric-property-use-cases)`,
          'image-src': require(`common/assets/images/custom-groups/custom-number.svg`),
        },
        [PropertyType.Datetime]: {
          ...COMMON_CUSTOMIZE_GROUPS_TOOLTIP_PROPS,
          text: `Select each time interval to display`,
          'image-src': require(`common/assets/images/custom-groups/custom-date.svg`),
        },
        [PropertyType.String]: {
          ...COMMON_CUSTOMIZE_GROUPS_TOOLTIP_PROPS,
          text: `Select which value(s) to display [Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#string-property-use-cases)`,
          'image-src': require(`common/assets/images/custom-groups/custom-string.svg`),
        },
        [PropertyType.List]: {
          ...COMMON_CUSTOMIZE_GROUPS_TOOLTIP_PROPS,
          text: `Select which value(s) to display [Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#string-property-use-cases)`,
          'image-src': require(`common/assets/images/custom-groups/custom-string.svg`),
        },
      }[simpleType],
    };
  },
  [ControlBarAction.AddMultiAttribution]: {
    value: {action: ControlBarAction.AddMultiAttribution},
    icon: `attribution-string`,
    label: `Attribution`,
    tooltip: {
      text: `Attribute credit to multiple touch-points in a user-journey that lead to conversion.`,
      size: DisplaySize.Small,
    },
  },
  [ControlBarAction.AddMultiAttributionBehaviorFilter]: {
    value: {action: ControlBarAction.AddMultiAttributionBehaviorFilter},
    icon: `filter-xs`,
    label: `Filter Touchpoints`,
    tooltip: {
      text: `Filter events with selected property values from being considered touchpoints for multi-touch attribution.`,
      size: DisplaySize.Small,
    },
  },
  [ControlBarAction.AddGroupedEvent]: {
    value: {action: ControlBarAction.AddGroupedEvent},
    icon: `event`,
    label: `Event Comparison`,
    tooltip: {
      text: `Choose multiple events per step to compare conversion rates.\n[More on comparison events](https://docs.mixpanel.com/docs/analysis/reports/funnels#comparison-events)`,
      size: DisplaySize.Small,
    },
  },
  [ControlBarAction.MapLookupTable]: {
    value: {action: ControlBarAction.MapLookupTable},
    icon: `lookup`,
    label: `Map Lookup Table`,
    tooltip: {
      text: `Enrich property with additional data using Lookup tables.\n[Learn more](https://docs.mixpanel.com/docs/tracking/how-tos/lookup-tables)`,
      size: DisplaySize.Small,
    },
  },
  [ControlBarAction.UnmapLookupTable]: {
    value: {action: ControlBarAction.UnmapLookupTable},
    icon: `lookup`,
    label: `Unmap Lookup Table`,
  },
  [ControlBarAction.Typecast]: (
    currentType: FullPropertyType,
    {disabledTypes = []}: {disabledTypes?: PropertyType[]} = {},
  ) => ({
    label: `Data Type`,
    icon: getIconForPropertyType(currentType),
    subSections: (() => {
      const isCartAnalysisEnabled = FeatureRollouts[FeatureRolloutName.CartAnalysis].isEnabled(
        currentProjectStore.value,
      );

      const getTypecastItems = (types: PropertyType[], isList: boolean) =>
        types.map((type) => {
          if (disabledTypes.includes(type)) {
            return null;
          }

          return {
            icon: !isList && getIconForPropertyType(type),
            isSelected: type === currentType.type && isList === currentType.isList,
            label: getLabelForPropertyType(type),
            value: {
              action: ControlBarAction.Typecast as ControlBarAction.Typecast,
              toType: {
                type,
                isList,
              },
            },
          };
        });

      const listItem = isCartAnalysisEnabled
        ? {
            icon: getIconForPropertyType(PropertyType.List),
            label: getLabelForPropertyType(PropertyType.List),
            subDisableFlip: true,
            subSections: [
              {
                items: getTypecastItems(TYPECAST_LIST_PROPERTY_SUBTYPES, true),
              },
            ],
          }
        : {
            // single List option for list of strings
            icon: getIconForPropertyType(PropertyType.List),
            label: getLabelForPropertyType(PropertyType.List),
            isSelected: currentType.isList,
            value: {
              action: ControlBarAction.Typecast as ControlBarAction.Typecast,
              toType: {
                type: PropertyType.String,
                isList: true,
              },
            },
          };

      const supportsList = !disabledTypes.includes(PropertyType.List);

      return [
        {
          items: [...getTypecastItems(TYPECAST_PROPERTY_TYPES, false), supportsList && listItem].filter(Boolean),
        },
      ];
    })(),
  }),
};

export const BehavioralActionItems: {[key in ControlBarAction]?: SectionItem} = {
  [ControlBarAction.Filter]: {
    label: `Where`,
    value: ControlBarAction.Filter,
    tooltip: {
      text: `Find users who match demographic details or belong to a cohort`,
      size: DisplaySize.Small,
    },
  },
};

export const DefaultCustomBucket: Bucket = {
  bucketSize: 10,
  disabled: false,
  groups: null,
  min: 0,
  max: 100,
  offset: null,
};

export const commonAddEntryTooltipAttrs = {
  size: DisplaySize.Small,
  placement: Placement.Right,
};

export const TutorialTooltips: {[key in SelectorTypeDefinitions]?: TooltipAttrs} = {
  [SelectorTypeDefinitions.Segment]: {
    'image-src': segmentationSvg,
    text: `Segment to group your data into different categories.`,
    ...commonAddEntryTooltipAttrs,
  },
  [SelectorTypeDefinitions.Filter]: {
    'image-src': filterSvg,
    text: `Filter to include or exclude specific data.`,
    ...commonAddEntryTooltipAttrs,
  },
};

const HELP_DOCS_LINK = `\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/insights#insights-data-functions-operators-and-calculations)`;

export const EventAggregations: SelectorTypeAggregation[] = [
  {
    label: `Total`,
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: `The total number of events performed${HELP_DOCS_LINK}`,
  },
  {
    label: `Unique`,
    operator: PRIMITIVE_MATH_TYPES.UNIQUE,
    description: ({pluralGroup}: AggregationLabelParams) =>
      `The number of ${pluralGroup} who performed an event${HELP_DOCS_LINK}`,
  },
  {
    label: ({group}: AggregationLabelParams) => `Total per ${group}`,
    subLabel: `Distribution, Average, Percentiles`,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description: ({group}: AggregationLabelParams) => `The number of events performed per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        label: `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Average across all users of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `A distribution of the number of times an event is performed per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) => `Median of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `25th Percentile`,
        operator: NUMERIC_MATH_TYPES.P25,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        shortLabel: `P25`,
        description: ({group}: AggregationLabelParams) =>
          `The 25th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `75th Percentile`,
        operator: NUMERIC_MATH_TYPES.P75,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        shortLabel: `P75`,
        description: ({group}: AggregationLabelParams) =>
          `The 75th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `90th Percentile`,
        operator: NUMERIC_MATH_TYPES.P90,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        shortLabel: `P90`,
        description: ({group}: AggregationLabelParams) =>
          `The 90th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Minimum of the number of time a ${group} performed an event${HELP_DOCS_LINK}`,
      },
      {
        label: `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Maximum of the number of time a ${group} performed an event${HELP_DOCS_LINK}`,
      },
    ],
  },
  {
    label: ({pluralGroupTitle}: AggregationLabelParams) => `Daily Active ${pluralGroupTitle} (DAU)`,
    operator: XAU_MATH_TYPES.DAU,
    shortLabel: `DAU`,
    description: ({pluralGroup}: AggregationLabelParams) =>
      `The number of ${pluralGroup} who performed an event within the last 24 hours${HELP_DOCS_LINK}`,
  },
  {
    label: ({pluralGroupTitle}: AggregationLabelParams) => `Weekly Active ${pluralGroupTitle} (WAU)`,
    operator: XAU_MATH_TYPES.WAU,
    shortLabel: `WAU`,
    description: ({pluralGroup}: AggregationLabelParams) =>
      `The number of ${pluralGroup} who performed an event within the last 7 days${HELP_DOCS_LINK}`,
  },
  {
    label: ({pluralGroupTitle}: AggregationLabelParams) => `Monthly Active ${pluralGroupTitle} (MAU)`,
    operator: XAU_MATH_TYPES.MAU,
    shortLabel: `MAU`,
    description: ({pluralGroup}: AggregationLabelParams) =>
      `The number of ${pluralGroup} who performed an event within the last 30 days${HELP_DOCS_LINK}`,
  },
  {
    label: `Count Sessions`,
    operator: PRIMITIVE_MATH_TYPES.SESSIONS,
    description: `The number of sessions that contain the selected event${HELP_DOCS_LINK}`,
    shortLabel: `Sessions`,
  },
];

const UserAggregations: SelectorTypeAggregation[] = [
  {
    label: `Total`,
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: `The number of profiles`,
  },
];

/** Behavior specific property aggregations */
export const BehaviorPropertyAggregations: SelectorTypeAggregation[] = [
  {
    forProperty: true,
    label: `Most Frequent`,
    operator: PRIMITIVE_MATH_TYPES.MOST_FREQUENT,
    shortLabel: `Most frequent`,
    description: `The most frequent property value across all instances of an event${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `First Value`,
    operator: PRIMITIVE_MATH_TYPES.FIRST_VALUE,
    shortLabel: `First value`,
    description: `The first property value across all instances of an event${HELP_DOCS_LINK}`,
  },
];

export const EventPropertyAggregations: SelectorTypeAggregation[] = [
  {
    forProperty: true,
    label: `Sum`,
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: `The total of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `Average`,
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    shortLabel: `Average`,
    description: `Average of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `Median`,
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    description: `Median of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `Distinct Count`,
    operator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
    shortLabel: `Distinct Count`,
    description: `The number of distinct property values across all instances of an event${HELP_DOCS_LINK}`,
  },
  /** @todo [pablo] have a dedicated list specific for behavioral aggregations */
  ...BehaviorPropertyAggregations,
  {
    forProperty: true,
    label: `25th Percentile`,
    operator: PRIMITIVE_MATH_TYPES.P25,
    shortLabel: `P25`,
    description: `The 25th percentile of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `75th Percentile`,
    operator: PRIMITIVE_MATH_TYPES.P75,
    shortLabel: `P75`,
    description: `The 75th percentile of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `90th Percentile`,
    operator: PRIMITIVE_MATH_TYPES.P90,
    shortLabel: `P90`,
    description: `The 90th percentile of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `99th Percentile`,
    operator: PRIMITIVE_MATH_TYPES.P99,
    shortLabel: `P99`,
    description: `The 99th percentile of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `Minimum`,
    operator: PRIMITIVE_MATH_TYPES.MIN,
    description: `Minimum of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `Maximum`,
    operator: PRIMITIVE_MATH_TYPES.MAX,
    description: `Maximum of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
  },
];

export const PerUserEventPropertyAggregations: SelectorTypeAggregation[] = [
  {
    forProperty: true,
    label: `Sum`,
    shortLabel: ({group}: AggregationLabelParams) => `Sum per ${group}`,
    perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: ({group}: AggregationLabelParams) =>
      `The total of numeric property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        forProperty: true,
        label: `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Average of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `A distribution of ${pluralGroup} by the sum of property values${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Median of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
        shortLabel: `P25`,
        description: ({group}: AggregationLabelParams) =>
          `The 25th percentile of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
        shortLabel: `P75`,
        description: ({group}: AggregationLabelParams) =>
          `The 75th percentile of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
        shortLabel: `P90`,
        description: ({group}: AggregationLabelParams) =>
          `The 90th percentile of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Minimum of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Maximum of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
    ],
  },
  {
    forProperty: true,
    label: `Average`,
    shortLabel: ({group}: AggregationLabelParams) => `Average per ${group}`,
    perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
    description: ({group}: AggregationLabelParams) =>
      `Average of numeric property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        forProperty: true,
        label: `Sum`,
        operator: PRIMITIVE_MATH_TYPES.TOTAL,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        description: ({group}: AggregationLabelParams) =>
          `Sum of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        description: ({group}: AggregationLabelParams) =>
          `Average of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `A distribution of ${pluralGroup} by the average of property values${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        description: ({group}: AggregationLabelParams) =>
          `Median of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        shortLabel: `P25`,
        description: ({group}: AggregationLabelParams) =>
          `The 25th percentile of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        shortLabel: `P75`,
        description: ({group}: AggregationLabelParams) =>
          `The 75th percentile of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        shortLabel: `P90`,
        description: ({group}: AggregationLabelParams) =>
          `The 90th percentile of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        description: ({group}: AggregationLabelParams) =>
          `Minimum of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        description: ({group}: AggregationLabelParams) =>
          `Maximum of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
    ],
  },
  {
    forProperty: true,
    label: `Distinct Count`,
    shortLabel: ({group}: AggregationLabelParams) => `Distinct Count per ${group}`,
    perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
    description: ({group}: AggregationLabelParams) =>
      `The number of distinct property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        forProperty: true,
        label: `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        description: ({group}: AggregationLabelParams) =>
          `Average of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Sum`,
        operator: PRIMITIVE_MATH_TYPES.TOTAL,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        description: ({group}: AggregationLabelParams) =>
          `Sum of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `A distribution of ${pluralGroup} by the distinct property value counts${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        description: ({group}: AggregationLabelParams) =>
          `The total of the distinct property values counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        shortLabel: `P25`,
        description: ({group}: AggregationLabelParams) =>
          `The 25th percentile of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        shortLabel: `P75`,
        description: ({group}: AggregationLabelParams) =>
          `The 75th percentile of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        shortLabel: `P90`,
        description: ({group}: AggregationLabelParams) =>
          `The 90th percentile of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        description: ({group}: AggregationLabelParams) =>
          `Minimum of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        description: ({group}: AggregationLabelParams) =>
          `Maximum of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
    ],
  },
  {
    forProperty: true,
    label: `Minimum`,
    shortLabel: ({group}: AggregationLabelParams) => `Minimum per ${group}`,
    perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
    description: ({group}: AggregationLabelParams) =>
      `Minimum of all the numeric property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        forProperty: true,
        label: `Sum`,
        operator: PRIMITIVE_MATH_TYPES.TOTAL,
        perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
        description: ({group}: AggregationLabelParams) =>
          `Sum of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
        description: ({group}: AggregationLabelParams) =>
          `Average of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `A distribution of ${pluralGroup} by the minimum of property values${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
        description: ({group}: AggregationLabelParams) =>
          `Median of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
        shortLabel: `P25`,
        description: ({group}: AggregationLabelParams) =>
          `The 25th percentile of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
        shortLabel: `P75`,
        description: ({group}: AggregationLabelParams) =>
          `The 75th percentile of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
        shortLabel: `P90`,
        description: ({group}: AggregationLabelParams) =>
          `The 90th percentile of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
        description: ({group}: AggregationLabelParams) =>
          `Maximum of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
    ],
  },
  {
    forProperty: true,
    label: `Maximum`,
    shortLabel: ({group}: AggregationLabelParams) => `Maximum per ${group}`,
    perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
    description: ({group}: AggregationLabelParams) =>
      `Maximum of all the numeric property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        forProperty: true,
        label: `Sum`,
        operator: PRIMITIVE_MATH_TYPES.TOTAL,
        perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
        description: ({group}: AggregationLabelParams) =>
          `Sum of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
        description: ({group}: AggregationLabelParams) => `Average of the maximum of property values per ${group}`,
      },
      {
        forProperty: true,
        label: `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `A distribution of ${pluralGroup} by the maximum of property values${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
        description: ({group}: AggregationLabelParams) =>
          `Median of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
        shortLabel: `P25`,
        description: ({group}: AggregationLabelParams) =>
          `The 25th percentile of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
        shortLabel: `P75`,
        description: ({group}: AggregationLabelParams) =>
          `The 75th percentile of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
        shortLabel: `P90`,
        description: ({group}: AggregationLabelParams) =>
          `The 90th percentile of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
        description: ({group}: AggregationLabelParams) =>
          `Minimum of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
    ],
  },
];

const UserPropertyAggregations: SelectorTypeAggregation[] = [
  {
    forProperty: true,
    label: `Sum`,
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: `Counts the total of a numeric property value across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `Average`,
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    description: `Adds the numeric property values across all profiles and divides by the total count${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `Median`,
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    description: `Counts the numeric property value across all profiles in order and finds the middle value${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `Distinct Count`,
    operator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
    description: `The number of distinct property values across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `25th Percentile`,
    operator: PRIMITIVE_MATH_TYPES.P25,
    shortLabel: `P25`,
    description: `Ranks the 25th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `75th Percentile`,
    operator: PRIMITIVE_MATH_TYPES.P75,
    shortLabel: `P75`,
    description: `Ranks the 75th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `90th Percentile`,
    operator: PRIMITIVE_MATH_TYPES.P90,
    shortLabel: `P90`,
    description: `Ranks the 90th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `99th Percentile`,
    operator: PRIMITIVE_MATH_TYPES.P99,
    shortLabel: `P99`,
    description: `Ranks the 99th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `Minimum`,
    operator: PRIMITIVE_MATH_TYPES.MIN,
    description: `The smallest value of a numeric property across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: `Maximum`,
    operator: PRIMITIVE_MATH_TYPES.MAX,
    description: `The greatest value of a numeric property across all profiles${HELP_DOCS_LINK}`,
  },
];

const EVENT_BASED_AGGREGATIONS = new Set<SelectorTypeAggregation>([
  ...EventAggregations,
  ...EventPropertyAggregations,
  ...PerUserEventPropertyAggregations,
]);

const USER_BASED_AGGREGATIONS = new Set<SelectorTypeAggregation>([...UserAggregations, ...UserPropertyAggregations]);

export const AggregationsMappings = new Map<SelectorType, Set<SelectorTypeAggregation>>([
  [ResourceType.Event, EVENT_BASED_AGGREGATIONS],
  [
    SelectorTypeDefinitions.Behavior,
    new Set([...Array.from(EVENT_BASED_AGGREGATIONS), ...BehaviorPropertyAggregations]),
  ],
  [ResourceType.User, USER_BASED_AGGREGATIONS],
]);

/* Defines the items to be displayed in an aggregation menu for all entity types */
export const NESTED_AGGREGATIONS_MENU_ITEMS: SelectorTypeAggregation[] = [
  // Total
  EventAggregations.find((item) => item.operator === PRIMITIVE_MATH_TYPES.TOTAL),
  // Uniques
  EventAggregations.find((item) => item.operator === PRIMITIVE_MATH_TYPES.UNIQUE),
  // Profile count for people mode
  UserAggregations.find((item) => item.operator === PRIMITIVE_MATH_TYPES.TOTAL),
  // Total per user
  EventAggregations.find((item) => item.perUserOperator === PER_USER_AGGREGATIONS.TOTAL),
  // Event property aggregations
  {
    label: `Aggregate Properties`,
    description: `Aggregate property value across all instances of an event${HELP_DOCS_LINK}`,
    subSections: EventPropertyAggregations,
  },
  // Profile property aggregations
  {
    label: `Aggregate Properties`,
    description: `Aggregate property value across all profiles${HELP_DOCS_LINK}`,
    subSections: UserPropertyAggregations,
  },
  // Rolling uniques (DAU, WAU, MAU)
  {
    label: ({pluralGroupTitle}: AggregationLabelParams) => `Count ${pluralGroupTitle}`,
    description: ({pluralGroup}: AggregationLabelParams) =>
      `The number of ${pluralGroup} who performed an event within a time period${HELP_DOCS_LINK}`,
    subSections: EventAggregations.filter(
      (item) =>
        item.operator === XAU_MATH_TYPES.DAU ||
        item.operator === XAU_MATH_TYPES.WAU ||
        item.operator === XAU_MATH_TYPES.MAU,
    ),
  },
  // Per-user event property aggregations
  {
    label: ({group}: AggregationLabelParams) => `Aggregate Properties per ${group}`,
    description: ({group}: AggregationLabelParams) =>
      `Aggregate property values across all instances of an event per ${group}${HELP_DOCS_LINK}`,
    subSections: PerUserEventPropertyAggregations,
  },
  // Session count
  EventAggregations.find((item) => item.operator === PRIMITIVE_MATH_TYPES.SESSIONS),
];

export function findTopLevelAggregation(selected: SelectorTypeAggregation): SelectorTypeAggregation {
  return NESTED_AGGREGATIONS_MENU_ITEMS.find(
    (item) => item === selected || item?.subSections?.some((subItem) => subItem === selected),
  );
}
